import type { ComponentProps } from "react"
import { formatNumber } from "@/v2-common/utils/utils.formatting"
import cn from "@/v2-ui/utils/utils.cn"
import PortfolioSectionHeader from "@/v2-backoffice-client/portfolio/PortfolioSectionHeader"
import UsageReportKpi from "@/v2-backoffice-client/usage/report/UsageReportKpi"
import { formatCompactAmount } from "@/v2-backoffice-client/customer/billing/customerBilling.utils"
import { RENEWAL_SOON_DAYS } from "@/v2-backoffice-client/portfolio/portfolio.constants"
import {
  derivePortfolioPlaybookSummary,
  type PortfolioPlay,
} from "@/v2-backoffice-client/portfolio/portfolio.plays.utils"
import { usageKpiGridClass } from "@/v2-backoffice-client/usage/report/usage.report.utils"
import PortfolioPlaybookChapters from "@/v2-backoffice-client/portfolio/PortfolioPlaybookChapters"

export type PortfolioPlaybookProps = ComponentProps<"div"> & {
  /** Every customer's plays (already globally filtered), in urgency order. */
  plays: PortfolioPlay[]
}

// The global playbook: the week's figures, then every customer's
// recommended plays, one card per account in urgency order. Deliberately
// STATELESS: there is no check-off. An item disappears when the underlying
// signal changes (the play worked) and CRM workflow stays in HubSpot.
function PortfolioPlaybook(props: PortfolioPlaybookProps) {
  const { plays, className, ...divProps } = props
  const summary = derivePortfolioPlaybookSummary(plays)

  return (
    <div className={cn("usage-report", className)} {...divProps}>
      <PortfolioSectionHeader
        eyebrow="Playbook"
        title="What to do this week"
        lead="One card per account, most urgent first: this week's calls, then the watch list, then openings and wins; within each, more money at stake before less. A name opens the account; the copy button lifts its plays into a HubSpot note."
        info="Ordered by urgency: actions before opportunities before wins, then high churn risk first, then the nearest renewal or trial date, then more money at stake (billed the last 12 months) before less. The list derives fresh from the last 28 days of usage — an item clears when its signal does, and follow-up workflow lives in HubSpot. The global filter bar (churn, activity, customers) scopes this list too."
      />

      <div className={usageKpiGridClass(4)}>
        <UsageReportKpi
          label="Accounts to act on"
          value={formatNumber(summary.actionAccounts)}
          tone={summary.actionAccounts > 0 ? "warning" : "default"}
          sub={[
            `at high churn risk, or renewing or ending a trial inside ${RENEWAL_SOON_DAYS} days`,
            summary.watchAccounts > 0
              ? `${formatNumber(summary.watchAccounts)} more to keep an eye on`
              : null,
          ]}
        />
        <UsageReportKpi
          label="Money at stake"
          value={
            summary.actionBilled > 0
              ? formatCompactAmount(summary.actionBilled, summary.currency)
              : "—"
          }
          sub={
            summary.actionBilled > 0
              ? "billed the last 12 months by the accounts to act on"
              : "no invoice history on the accounts to act on"
          }
        />
        <UsageReportKpi
          label="Openings"
          value={formatNumber(summary.openings)}
          sub={`expansion angles across ${formatNumber(summary.openingAccounts)} ${
            summary.openingAccounts === 1 ? "account" : "accounts"
          }`}
        />
        <UsageReportKpi
          label="Wins"
          value={formatNumber(summary.wins)}
          sub={`good stories across ${formatNumber(summary.winAccounts)} ${
            summary.winAccounts === 1 ? "account" : "accounts"
          }: a renewal to anchor, a reference to ask`}
        />
      </div>

      <PortfolioPlaybookChapters plays={plays} />

      <p className="text-xs text-muted-foreground">
        The plays derive fresh from the last 28 days of usage: one disappears
        when its signal does, which is the sign it worked. There is nothing to
        tick off here; follow-up lives in HubSpot.
      </p>
    </div>
  )
}

export default PortfolioPlaybook
